import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { z } from "zod";

import type { AppDispatch } from "@/app/store/store";
import { useAppSelector } from "@/app/store/store";
import { useUserContext } from "@/shared/features/user-context/presentation/hooks/useUserContext";
import { pincodeBuild } from "../../config/pincode.build";
import {
  selectIsPincodeConfirmed,
  selectPincode,
  selectPincodeSid,
  setPincodeState,
} from "../store/pincodeSlice";

interface UsePincodeResult {
  pincode: string | undefined;
  sid: string | undefined;
  inputValue: string;
  error: string | null;
  isPopupOpen: boolean;
  openPopup: (onSaved?: (nextSid?: string) => void) => void;
  closePopup: () => void;
  handleChange: (value: string) => void;
  handleSubmit: () => Promise<boolean>;
}

const pincodeSchema = z
  .string()
  .trim()
  .regex(/^[1-9][0-9]{5}$/, "Please enter a valid 6-digit pincode.");

export function usePincode(): UsePincodeResult {
  const dispatch = useDispatch<AppDispatch>();
  const { service } = useMemo(() => pincodeBuild(dispatch), [dispatch]);

  const reduxPincode = useAppSelector(selectPincode);
  const reduxSid = useAppSelector(selectPincodeSid);
  const isConfirmed = useAppSelector(selectIsPincodeConfirmed);

  const { userContext } = useUserContext();

  useEffect(() => {
    if (reduxPincode) return;

    const resolved = service.resolveInitialPincode({
      cachedPincode: service.getCachedPincode(),
      isPincodeUpdated: service.isPincodeUpdated(),
      userContextPincode: userContext?.pincode ?? null,
      guessedPincode: userContext?.guessedPincode ?? true,
    });

    if (resolved) {
      const ctx = service.getCachedPincodeContext();
      dispatch(
        setPincodeState({
          pincode: resolved,
          sid: ctx?.sid ?? null,
          isConfirmed: service.isPincodeUpdated(),
        })
      );
    }
  }, [dispatch, service, userContext, reduxPincode]);

  const pincode = reduxPincode ?? undefined;
  const sid = reduxSid ?? undefined;

  const confirmedInputValue = pincode && isConfirmed ? pincode : "";
  const [draft, setDraft] = useState<string | null>(null);
  const inputValue = draft ?? confirmedInputValue;
  const [error, setError] = useState<string | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const onSavedRef = useRef<((nextSid?: string) => void) | null>(null);

  const openPopup = useCallback((onSaved?: (nextSid?: string) => void) => {
    onSavedRef.current = onSaved ?? null;
    setIsPopupOpen(true);
  }, []);

  const closePopup = useCallback(() => {
    onSavedRef.current = null;
    setIsPopupOpen(false);
  }, []);

  const handleChange = useCallback(
    (value: string) => {
      const numeric = value.replace(/\D/g, "").slice(0, 6);
      setDraft(numeric);
      if (error) {
        setError(null);
      }
    },
    [error]
  );

  const handleSubmit = useCallback(async (): Promise<boolean> => {
    const result = pincodeSchema.safeParse(inputValue);
    if (!result.success) {
      const firstIssue = result.error.issues[0];
      setError(firstIssue?.message ?? "Please enter a valid 6-digit pincode.");
      return false;
    }

    const nextPincode = result.data;
    try {
      const details = await service.updatePincodeContext(nextPincode);
      if (!details) {
        setError("Unable to fetch pincode details. Please try again.");
        return false;
      }

      const nextSid = details.sid ?? null;
      dispatch(
        setPincodeState({
          pincode: nextPincode,
          sid: nextSid,
          isConfirmed: true,
        })
      );

      setDraft(null);
      setError(null);
      setIsPopupOpen(false);

      const cb = onSavedRef.current;
      onSavedRef.current = null;
      cb?.(nextSid ?? undefined);
      return true;
    } catch {
      setError("Unable to fetch pincode details. Please try again.");
      return false;
    }
  }, [dispatch, inputValue, service]);

  return {
    pincode,
    sid,
    inputValue,
    error,
    isPopupOpen,
    openPopup,
    closePopup,
    handleChange,
    handleSubmit,
  };
}
